import { useAsync, UseAsyncStatus } from '@shared/async';
import { useErrorHandler } from '@shared/errors';
import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { HiXMark } from 'react-icons/hi2';

import { Badge } from '@/components-new/badge';
import { Button } from '@/components-new/button';
import { Checkbox, CheckboxField, CheckboxGroup } from '@/components-new/checkbox';
import { Combobox } from '@/components-new/combobox';
import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogTitle,
} from '@/components-new/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from '@/components-new/fieldset';
import { Input } from '@/components-new/input';
import { Radio, RadioField, RadioGroup } from '@/components-new/radio';
import {
  AddDrugPackagingsRequest,
  useMarketBasketService,
} from '@/features/market-baskets/api/use-market-baskets-service';
import { useProductsLookup } from '@/hooks/use-lookups';
import { useLookupsService } from '@/hooks/use-lookups-service';
import { notifySuccess, notifyWarn } from '@/lib/notification/notifications';
import { CoverageTagLookup, CoverageTagPackaging } from '@/types/coverage-tag-lookup';
import { Lookup } from '@/types/lookup';

type SelectionMode = 'drug_coverage_tag' | 'ndcs';

type FormInputs = {
  mode: SelectionMode;
  drug?: Lookup;
};

type AddDrugPackagingsDialogProps = {
  open: boolean;
  marketBasketId: number;
  onClose: () => void;
  onSuccess: () => void;
};

export const AddDrugPackagingsDialog = ({
  open,
  marketBasketId,
  onClose,
  onSuccess,
}: AddDrugPackagingsDialogProps) => {
  const { addDrugPackagings } = useMarketBasketService();
  const { getRawCoverageTags } = useLookupsService();
  const { handleError } = useErrorHandler();

  const [drugQuery, setDrugQuery] = useState<string | undefined>(undefined);
  const [enteredNdcs, setEnteredNdcs] = useState<string[]>([]);
  const [ndcInputValue, setNdcInputValue] = useState('');
  const [ndcInputError, setNdcInputError] = useState<string | undefined>(undefined);
  const [ndcBulkMode, setNdcBulkMode] = useState(false);
  const [coverageTags, setCoverageTags] = useState<CoverageTagLookup[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [includeUntagged, setIncludeUntagged] = useState(false);
  const [untaggedCount, setUntaggedCount] = useState(0);
  const [untaggedPackagings, setUntaggedPackagings] = useState<CoverageTagPackaging[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const { data: drugs = [] } = useProductsLookup(drugQuery, marketBasketId);

  const { control, handleSubmit, reset } = useForm<FormInputs>({
    mode: 'all',
    defaultValues: { mode: 'drug_coverage_tag' },
  });

  const mode = useWatch({ control, name: 'mode' });
  const drug = useWatch({ control, name: 'drug' });

  const addAsync = useAsync(addDrugPackagings);
  const saving = addAsync.status === UseAsyncStatus.Pending;

  useEffect(() => {
    if (!open) return;
    reset({ mode: 'drug_coverage_tag' });
    setEnteredNdcs([]);
    setNdcInputValue('');
    setNdcInputError(undefined);
    setNdcBulkMode(false);
    setCoverageTags([]);
    setSelectedTagIds([]);
    setIncludeUntagged(false);
    setUntaggedCount(0);
    setUntaggedPackagings([]);
    setDrugQuery(undefined);
  }, [open, reset]);

  useEffect(() => {
    setCoverageTags([]);
    setSelectedTagIds([]);
    setIncludeUntagged(false);
    setUntaggedCount(0);
    setUntaggedPackagings([]);
    if (mode !== 'drug_coverage_tag' || !drug?.id) return;

    setLoadingTags(true);
    getRawCoverageTags({ drugId: drug.id, rpp: 100 })
      .then((result) => {
        setCoverageTags(result.items);
        setUntaggedCount(result.untaggedPackagingCount ?? 0);
        setUntaggedPackagings(result.untaggedPackagings ?? []);
      })
      .catch(() => { setCoverageTags([]); setUntaggedCount(0); setUntaggedPackagings([]); })
      .finally(() => setLoadingTags(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drug?.id, mode]);

  useEffect(() => {
    const { status, error } = addAsync;
    if (status === UseAsyncStatus.Pending || status === UseAsyncStatus.Idle) return;

    if (status === UseAsyncStatus.Error) {
      handleError(error, {
        title: 'Add Failed',
        message: 'Unable to add drug packagings to market basket.',
      });
      return;
    }

    const result = addAsync.value;
    notifySuccess({
      title: 'Packagings Added',
      message:
        result?.noneFound
          ? mode === 'drug_coverage_tag'
            ? 'No packagings were found for the selected drug and coverage tag(s).'
            : 'No packagings were found for the selected NDC(s).'
          : result?.added === 0
            ? 'All matching packagings were already in this market basket.'
            : `Added ${result?.added} packaging(s) to the market basket.`,
    });
    if (result?.missingWac && result.missingWac > 0) {
      notifyWarn({
        title: 'WAC Pricing Missing',
        message: `${result.missingWac} packaging(s) have no WAC pricing data in any market basket. Use the override pencil on those rows to supply pricing manually.`,
      });
    }
    onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addAsync.status]);

  const addNdc = () => {
    const trimmed = ndcInputValue.trim().replace(/-/g, '');
    if (!trimmed) return;
    if (!/^\d{11}$/.test(trimmed)) {
      setNdcInputError('NDC must be exactly 11 digits.');
      return;
    }
    if (enteredNdcs.includes(trimmed)) {
      setNdcInputError('This NDC has already been added.');
      return;
    }
    setNdcInputError(undefined);
    setEnteredNdcs((prev) => [...prev, trimmed]);
    setNdcInputValue('');
  };

  const handleNdcKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNdc();
    }
  };

  const parseBulkNdcs = (raw: string) => {
    const candidates = raw.split(/[\s,;|\n]+/).map(s => s.trim().replace(/-/g, '')).filter(Boolean);
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const c of candidates) {
      if (!/^\d{11}$/.test(c)) { invalid.push(c); continue; }
      if (!valid.includes(c) && !enteredNdcs.includes(c)) valid.push(c);
    }
    if (invalid.length > 0) {
      setNdcInputError(`${invalid.length} invalid NDC(s) skipped: ${invalid.slice(0, 5).join(', ')}${invalid.length > 5 ? '…' : ''}`);
    } else {
      setNdcInputError(undefined);
    }
    if (valid.length > 0) {
      setEnteredNdcs(prev => [...prev, ...valid]);
      setNdcInputValue('');
    }
  };

  const toggleTag = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const onSubmit = (data: FormInputs) => {
    const request: AddDrugPackagingsRequest = {};

    if (data.mode === 'ndcs') {
      if (enteredNdcs.length === 0) return;
      request.ndcs = enteredNdcs;
    } else {
      if (!data.drug?.id || (selectedTagIds.length === 0 && !includeUntagged)) return;
      request.drugId = data.drug.id;
      request.coverageTagIds = selectedTagIds;
      request.includeUntagged = includeUntagged;
    }

    void addAsync.execute(marketBasketId, request);
  };

  const selectableTags = coverageTags.filter((t) => t.packagingCount > 0);

  const submitDisabled =
    saving ||
    (mode === 'ndcs' && enteredNdcs.length === 0) ||
    (mode === 'drug_coverage_tag' && (!drug?.id || (selectedTagIds.length === 0 && !includeUntagged)));

  return (
    <Dialog open={open} onClose={onClose} size="3xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Add Existing Drugs</DialogTitle>
        <DialogDescription>
          Select drugs already in Capsule to add their packagings to this market
          basket without re-uploading a Gold Standard CSV. Pricing data will be
          copied from an existing market basket where the drug is already tracked.
        </DialogDescription>

        <Field>
          <Label>Selection method</Label>
          <Controller
            control={control}
            name="mode"
            render={({ field: { onChange, value } }) => (
              <RadioGroup value={value} onChange={onChange}>
                <RadioField>
                  <Radio value="drug_coverage_tag" />
                  <Label>By drug + coverage tag(s)</Label>
                </RadioField>
                <RadioField>
                  <Radio value="ndcs" />
                  <Label>By specific NDC(s)</Label>
                </RadioField>
              </RadioGroup>
            )}
          />
        </Field>

        {mode === 'drug_coverage_tag' && (
          <>
            <Controller
              control={control}
              name="drug"
              rules={{ required: 'Drug is required' }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Field>
                  <Label>Drug</Label>
                  <Combobox
                    options={drugs}
                    value={value}
                    onChange={onChange}
                    onQueryChange={setDrugQuery}
                    valueKey="id"
                    labelKey="label"
                    placeholder="Search for a drug..."
                  />
                  {error && <ErrorMessage>{error.message}</ErrorMessage>}
                </Field>
              )}
            />

            <Field disabled={!drug?.id || loadingTags}>
              <Label>Coverage tag(s)</Label>
              {!drug?.id ? (
                <Description>Select a drug first to see its coverage tags.</Description>
              ) : loadingTags ? (
                <Description>Loading tags…</Description>
              ) : selectableTags.length === 0 && untaggedCount === 0 ? (
                <Description>No coverage tags with packagings found for this drug.</Description>
              ) : (
                <CheckboxGroup className="max-h-96 overflow-y-auto pr-1">
                  {selectableTags.map((tag) => (
                    <div key={tag.coverageTagId} className="space-y-1">
                      <CheckboxField>
                        <Checkbox
                          checked={selectedTagIds.includes(tag.coverageTagId)}
                          onChange={() => toggleTag(tag.coverageTagId)}
                        />
                        <Label>
                          {tag.coverageTagName}{tag.isDefault ? ' (default)' : ''} &mdash; {tag.packagingCount} packaging{tag.packagingCount !== 1 ? 's' : ''}
                        </Label>
                      </CheckboxField>
                      {tag.packagings && tag.packagings.length > 0 && (
                        <div className="pl-8">
                          <PackagingList packagings={tag.packagings} />
                        </div>
                      )}
                    </div>
                  ))}
                  {untaggedCount > 0 && (
                    <div className="space-y-1">
                      <CheckboxField>
                        <Checkbox
                          checked={includeUntagged}
                          onChange={() => setIncludeUntagged((prev) => !prev)}
                        />
                        <Label>
                          Untagged &mdash; {untaggedCount} packaging{untaggedCount !== 1 ? 's' : ''}
                        </Label>
                      </CheckboxField>
                      {untaggedPackagings.length > 0 && (
                        <div className="pl-8">
                          <PackagingList packagings={untaggedPackagings} />
                        </div>
                      )}
                    </div>
                  )}
                </CheckboxGroup>
              )}
            </Field>
          </>
        )}

        {mode === 'ndcs' && (
          <Field>
            <div className="flex items-center justify-between">
              <Label>NDC(s)</Label>
              <button
                type="button"
                className="text-xs text-zinc-500 underline-offset-2 hover:text-zinc-700 hover:underline"
                onClick={() => { setNdcBulkMode(v => !v); setNdcInputValue(''); setNdcInputError(undefined); }}
              >
                {ndcBulkMode ? 'Single entry' : 'Paste multiple'}
              </button>
            </div>
            {ndcBulkMode ? (
              <div className="space-y-2">
                <textarea
                  className="block w-full rounded-lg border border-zinc-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  rows={6}
                  placeholder={'Paste NDCs separated by commas, spaces, or new lines:\n00071015523\n00071015524, 00071015525'}
                  value={ndcInputValue}
                  onChange={e => { setNdcInputValue(e.target.value); setNdcInputError(undefined); }}
                />
                <Button
                  type="button"
                  outline
                  onClick={() => parseBulkNdcs(ndcInputValue)}
                  disabled={!ndcInputValue.trim()}
                >
                  Add All
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. 00071015523"
                  value={ndcInputValue}
                  onChange={(e) => { setNdcInputValue(e.target.value); setNdcInputError(undefined); }}
                  onKeyDown={handleNdcKeyDown}
                  invalid={!!ndcInputError}
                />
                <Button
                  type="button"
                  outline
                  onClick={addNdc}
                  disabled={!ndcInputValue.trim()}
                >
                  Add
                </Button>
              </div>
            )}
            {ndcInputError && <ErrorMessage>{ndcInputError}</ErrorMessage>}
            {enteredNdcs.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {enteredNdcs.map((ndc) => (
                  <Badge key={ndc} color="zinc">
                    {ndc}
                    <Button
                      plain
                      type="button"
                      onClick={() => setEnteredNdcs((prev) => prev.filter((n) => n !== ndc))}
                      aria-label={`Remove ${ndc}`}
                    >
                      <HiXMark data-slot="icon" />
                    </Button>
                  </Badge>
                ))}
                {enteredNdcs.length > 1 && (
                  <button
                    type="button"
                    className="text-xs text-zinc-400 underline-offset-2 hover:text-red-500 hover:underline"
                    onClick={() => setEnteredNdcs([])}
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}
          </Field>
        )}

        <DialogActions>
          <Button outline onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={submitDisabled}>
            {saving ? 'Adding…' : 'Add Packagings'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const PackagingList = ({ packagings }: { packagings: CoverageTagPackaging[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        className="text-xs text-zinc-400 underline-offset-2 hover:text-zinc-600 hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide NDCs' : 'Show NDCs'}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-0.5">
          {packagings.map((p) => (
            <li key={p.ndc} className="text-xs text-zinc-600">
              <span className="font-mono">{p.ndc}</span> &mdash; {p.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
